import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ChevronDown, ChevronRight, Download, AlertTriangle, Check, Filter, DollarSign, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const formatINR = (val) => {
  if (!val) return '0';
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return `${val.toLocaleString('en-IN')}`;
};

const CATEGORY_ICONS = {
  INVESTOR_PAYMENTS: { icon: ArrowDownRight, color: 'text-emerald-400' },
  ESCROW_TO_LLP: { icon: ArrowUpRight, color: 'text-blue-400' },
  LLP_TO_DEVELOPER: { icon: ArrowUpRight, color: 'text-purple-400' },
  BROKERAGE_TO_PLATFORM: { icon: ArrowDownRight, color: 'text-amber-400' },
  BROKERAGE_TO_BROKERS: { icon: ArrowUpRight, color: 'text-orange-400' },
};

const STATUS_BADGE = {
  COMPLETE: 'bg-emerald-500/20 text-emerald-400',
  PARTIAL: 'bg-amber-500/20 text-amber-400',
  PENDING: 'bg-blue-500/20 text-blue-400',
  FAILED: 'bg-red-500/20 text-red-400',
  EMPTY: 'bg-gray-500/20 text-gray-500',
};

const TXN_STATUS = {
  COMPLETED: 'text-emerald-400',
  PENDING: 'text-amber-400',
  FAILED: 'text-red-400',
};

const ReconCheck = ({ recon, label }) => {
  if (!recon) return null;
  const isOk = recon.match;
  const diff = recon.difference || 0;
  if (diff === 0 && isOk) return null; // Don't show if everything matches with no transactions

  return (
    <div className={`flex items-center gap-2 text-xs px-3 py-1.5 ${isOk ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
      {isOk ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      <span>{label}: {isOk ? 'Matched' : `Diff: ${formatINR(Math.abs(diff))}`}</span>
      {recon.note && <span className="text-gray-500 ml-2">({recon.note})</span>}
    </div>
  );
};

export default function FinancialLedger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState(null);
  const [projectFilter, setProjectFilter] = useState('');
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedUnits, setExpandedUnits] = useState({});
  const [expandedCats, setExpandedCats] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (projectFilter) params.set('project_filter', projectFilter);
      const [ledgerRes, reconRes] = await Promise.all([
        api.get(`/admin/financial-ledger/hierarchical?${params}`),
        api.get('/admin/data-integrity/reconciliation'),
      ]);
      setData(ledgerRes.data);
      setAlerts(reconRes.data);
      // Auto-expand first project
      if (ledgerRes.data?.projects?.length > 0) {
        setExpandedProjects(prev => ({ ...prev, [ledgerRes.data.projects[0].projectId]: true }));
      }
    } catch { toast.error('Failed to load financial ledger'); }
    finally { setLoading(false); }
  }, [projectFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleProject = (pid) => setExpandedProjects(p => ({ ...p, [pid]: !p[pid] }));
  const toggleUnit = (uid) => setExpandedUnits(p => ({ ...p, [uid]: !p[uid] }));
  const toggleCat = (key) => setExpandedCats(p => ({ ...p, [key]: !p[key] }));

  const globalTotals = data?.globalTotals || {};
  const projects = data?.projects || [];

  return (
    <div className="p-8" data-testid="financial-ledger">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl text-white font-light">Financial Ledger</h1>
          <p className="text-sm text-gray-500 mt-1">Project &gt; Unit &gt; Category hierarchy with reconciliation</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchData} className="p-2 border border-white/10 text-gray-400 hover:text-white">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Alerts banner */}
      {alerts && alerts.alertCount > 0 && (
        <div className="mb-6 p-4 border border-red-500/30 bg-red-500/5" data-testid="reconciliation-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-400 font-medium">{alerts.alertCount} Reconciliation Alert{alerts.alertCount > 1 ? 's' : ''}</span>
            {alerts.criticalCount > 0 && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5">{alerts.criticalCount} critical</span>}
          </div>
          <div className="space-y-1">
            {alerts.alerts?.slice(0, 5).map((alert, i) => (
              <div key={i} className="text-xs text-gray-400 flex items-center gap-2">
                <span className={`px-1.5 py-0.5 ${alert.level === 'CRITICAL' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  {alert.level}
                </span>
                <span className="text-white">{alert.unitName}:</span>
                <span>{alert.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Global totals */}
      {!loading && Object.values(globalTotals).some(v => v > 0) && (
        <div className="grid grid-cols-5 gap-3 mb-6" data-testid="global-totals">
          {Object.entries(data?.categoryDefinitions || {}).map(([key, def]) => {
            const cfg = CATEGORY_ICONS[key] || {};
            const Icon = cfg.icon || DollarSign;
            return (
              <div key={key} className="p-3 border border-white/10 bg-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-3.5 h-3.5 ${cfg.color || 'text-gray-400'}`} />
                  <span className="text-xs text-gray-500">{def.label}</span>
                </div>
                <p className="font-mono text-white">{formatINR(globalTotals[key] || 0)}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3 mb-6">
        <Filter className="w-4 h-4 text-gray-500" />
        <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
          className="bg-white/5 border border-white/10 px-3 py-2 text-sm text-gray-300" data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.projectId} value={p.projectId}>{p.projectName}</option>)}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading financial data...</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 text-gray-500">No financial data found</div>
      ) : (
        projects.map(project => (
          <div key={project.projectId} className="mb-6" data-testid={`project-ledger-${project.projectId}`}>
            {/* Project header */}
            <button onClick={() => toggleProject(project.projectId)}
              className="w-full flex items-center justify-between p-4 border border-white/10 hover:bg-white/5 text-left">
              <div className="flex items-center gap-3">
                {expandedProjects[project.projectId] ? <ChevronDown className="w-4 h-4 text-[#4ADE80]" /> : <ChevronRight className="w-4 h-4 text-[#4ADE80]" />}
                <span className="text-base text-white">{project.projectName}</span>
                <span className="text-xs text-gray-500">{project.location}</span>
              </div>
            </button>

            {expandedProjects[project.projectId] && (
              <div className="ml-6 mt-2 space-y-2">
                {project.units.map(unit => (
                  <div key={unit.unitId} className="border border-white/10" data-testid={`unit-ledger-${unit.unitId}`}>
                    {/* Unit header */}
                    <button onClick={() => toggleUnit(unit.unitId)}
                      className="w-full flex items-center justify-between p-3 hover:bg-white/5 text-left">
                      <div className="flex items-center gap-3">
                        {expandedUnits[unit.unitId] ? <ChevronDown className="w-3.5 h-3.5 text-gray-500" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-500" />}
                        <span className="text-sm text-white">{unit.unitName}</span>
                        <span className="text-xs text-gray-500">{unit.state}</span>
                        {unit.health === 'alert' && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{unit.totalTransactions} transactions</span>
                        <span className="font-mono text-white">{formatINR(unit.capitalRaised)} / {formatINR(unit.capitalTarget)}</span>
                      </div>
                    </button>

                    {expandedUnits[unit.unitId] && (
                      <div className="border-t border-white/5">
                        {/* Categories */}
                        {Object.entries(unit.categories || {}).map(([catKey, cat]) => {
                          const catIconCfg = CATEGORY_ICONS[catKey] || {};
                          const CatIcon = catIconCfg.icon || DollarSign;
                          const isExpCat = expandedCats[`${unit.unitId}_${catKey}`];

                          return (
                            <div key={catKey} className="border-b border-white/5 last:border-b-0">
                              <button onClick={() => toggleCat(`${unit.unitId}_${catKey}`)}
                                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 text-left"
                                data-testid={`category-${unit.unitId}-${catKey}`}>
                                <div className="flex items-center gap-3">
                                  {isExpCat ? <ChevronDown className="w-3 h-3 text-gray-600" /> : <ChevronRight className="w-3 h-3 text-gray-600" />}
                                  <CatIcon className={`w-3.5 h-3.5 ${catIconCfg.color || 'text-gray-400'}`} />
                                  <span className="text-sm text-gray-300">{cat.label}</span>
                                  {cat.failedCount > 0 && (
                                    <span className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5">{cat.failedCount} failed</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4">
                                  <span className={`text-xs px-2 py-0.5 ${STATUS_BADGE[cat.status] || STATUS_BADGE.EMPTY}`}>{cat.status}</span>
                                  <span className="font-mono text-sm text-white">{formatINR(cat.total)}</span>
                                  {cat.pending > 0 && <span className="font-mono text-xs text-amber-400">+{formatINR(cat.pending)} pending</span>}
                                </div>
                              </button>

                              {/* Transactions */}
                              {isExpCat && cat.transactions?.length > 0 && (
                                <div className="bg-black/20">
                                  {cat.transactions.map(txn => (
                                    <div key={txn.transactionId} className="flex items-center justify-between px-8 py-2 border-t border-white/5 text-xs">
                                      <div className="flex items-center gap-4">
                                        <span className="text-gray-600 font-mono">{txn.transactionId}</span>
                                        <span className="text-gray-400">{txn.type}</span>
                                        {txn.investorName && <span className="text-gray-500">Investor: {txn.investorName}</span>}
                                        {txn.brokerName && <span className="text-gray-500">Broker: {txn.brokerName}</span>}
                                      </div>
                                      <div className="flex items-center gap-4">
                                        <span className={TXN_STATUS[txn.status] || 'text-gray-400'}>{txn.status}</span>
                                        <span className="font-mono text-white">{formatINR(txn.amount)}</span>
                                        <span className="text-gray-600">{txn.createdAt ? new Date(txn.createdAt).toLocaleDateString() : ''}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {isExpCat && cat.transactions?.length === 0 && (
                                <div className="px-8 py-3 text-xs text-gray-600 bg-black/20">No transactions</div>
                              )}
                            </div>
                          );
                        })}

                        {/* Reconciliation */}
                        <div className="px-4 py-3 bg-white/[0.02] space-y-1" data-testid={`recon-${unit.unitId}`}>
                          <ReconCheck recon={unit.reconciliation?.investorVsEscrow} label="Investor Payments vs Escrow" />
                          <ReconCheck recon={unit.reconciliation?.escrowVsDeveloper} label="Escrow vs Developer + Brokerage" />
                          <ReconCheck recon={unit.reconciliation?.brokerPayoutsVsPool} label="Broker Payouts vs Pool" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
